import React, { useMemo, useState } from 'react';
import { SafeAreaView, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CaptureReview } from './CaptureReview';
import type { EditableDetectedItem } from '../../types/foodRecognition';
import { formatDeliveryDate } from './PantryImportBanner';
import { deliveryRows, planDeliveryReview, toDetectedItems } from './deliveryReviewPlan';
import { buildMoveUpdates } from './usePantryActions';
import {
  markImportReviewedAndSync,
  removeItemAndSync,
  selectActiveInventory,
  updateItemAndSync,
} from '../../redux/inventorySlice';
import { learnZone, savePantryLearning } from '../../redux/pantryLearningSlice';
import { markPantryUpdated } from '../../redux/pantrySyncSlice';
import { primaryKey } from '../../utils/pantryDuplicates';
import { trackEvent } from '../../services/analyticsService';
import { Paprika } from '../../styles/theme';
import { RootState, AppDispatch } from '../../redux/store';

interface Props {
  importId: string;
  /** Closed without confirming: the banner stays, nothing is written. */
  onClose: () => void;
  /** Confirmed; the message is for the pantry's toast. */
  onDone: (message: string) => void;
}

/**
 * One delivery, reviewed on the receipt scan's screen.
 *
 * The rows are read once, when the review opens (callers key it on the
 * import). Reading them live would rebuild the list under the cook's thumb
 * every time a sync lands, and `CaptureReview` would throw their edits away.
 */
export function DeliveryReview({ importId, onClose, onDone }: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const inventory = useSelector(selectActiveInventory);
  const userId = useSelector((state: RootState) => state.auth?.user?.uid);
  const householdId = useSelector((state: RootState) => state.auth?.householdId ?? null);

  const [rows] = useState(() => deliveryRows(inventory, importId));
  const result = useMemo(() => ({ detected_items: toDetectedItems(rows, i18n.language) }), [rows, i18n.language]);

  const deliveryDate = rows.find(row => row.deliveryDate)?.deliveryDate;
  const date = deliveryDate ? formatDeliveryDate(deliveryDate, i18n.language) : '';

  const handleConfirm = (_accepted: EditableDetectedItem[], reviewed: EditableDetectedItem[]) => {
    const plan = planDeliveryReview(rows, reviewed);

    for (const item of plan.remove) {
      dispatch(removeItemAndSync({ itemId: item.id, userId }));
    }
    for (const { item, zone } of plan.moves) {
      const updates = buildMoveUpdates(item, zone);
      dispatch(updateItemAndSync({ id: item.id, updates: { ...updates, needsSorting: false }, userId }));
    }
    const learned = plan.moves.filter(move => move.learned);
    if (learned.length > 0) {
      for (const { item, zone } of learned) dispatch(learnZone({ key: primaryKey(item.name), zone }));
      dispatch(savePantryLearning({ userId, householdId }));
    }
    dispatch(markImportReviewedAndSync({ importId, userId }));
    dispatch(markPantryUpdated());

    trackEvent('pantry_import_reviewed', {
      importId,
      kept: plan.kept,
      removed: plan.remove.length,
      moved: plan.moves.length,
      corrected: learned.length,
    });
    onDone(t('pantry.import.reviewedToast', { count: plan.kept }));
  };

  if (rows.length === 0) return null;

  return (
    <SafeAreaView style={styles.root} testID="delivery-review">
      <CaptureReview
        mode="receipt"
        result={result}
        inventory={inventory}
        flagDuplicates={false}
        labels={{
          title: date ? t('pantry.import.title', { date }) : t('pantry.import.titleNoDate'),
          subtitle: t('pantry.import.reviewSub'),
          cancel: t('pantry.import.later'),
          confirm: (count) => t('pantry.import.keep', { count }),
        }}
        onConfirm={handleConfirm}
        onCancel={onClose}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
});
